'use client'

import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigationData } from '@/lib/context/NavigationProvider'

// Only show in development
const IS_DEVELOPMENT = process.env.NODE_ENV === 'development'

interface DevPanelProps {
  show?: boolean
}

export function DevPanel({ show = false }: DevPanelProps) {
  const [isVisible, setIsVisible] = useState(show)
  const [logs, setLogs] = useState<string[]>([])
  const [systemInfo, setSystemInfo] = useState<any>({})
  const queryClient = useQueryClient()
  const { gameweekInfo, isLoading } = useNavigationData()

  useEffect(() => {
    // Only run effects in development
    if (!IS_DEVELOPMENT) return

    // Update status periodically
    const interval = setInterval(() => {
      const timestamp = new Date().toLocaleTimeString()
      setLogs((prev) => {
        const newLog = `${timestamp}: DevPanel update`
        return [...prev.slice(-19), newLog] // Keep last 20 logs
      })

      // Update system info
      setSystemInfo({
        timestamp: new Date().toISOString(),
        url: typeof window !== 'undefined' ? window.location.pathname : '',
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent.split(' ')[0] : 'N/A',
        viewport:
          typeof window !== 'undefined' ? `${window.innerWidth}x${window.innerHeight}` : 'N/A'
      })
    }, 5000)

    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    // Only run effects in development
    if (!IS_DEVELOPMENT) return

    // Add keyboard shortcut to toggle panel (Ctrl+Shift+D)
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.shiftKey && event.key === 'D') {
        setIsVisible((prev) => !prev)
      }
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('keydown', handleKeyPress)
      return () => window.removeEventListener('keydown', handleKeyPress)
    }
  }, [])

  // Don't render in production
  if (!IS_DEVELOPMENT) {
    return null
  }

  if (!isVisible) {
    return (
      <div className="fixed bottom-4 right-4 z-50">
        <Button
          onClick={() => setIsVisible(true)}
          variant="outline"
          size="sm"
          className="bg-white/90 backdrop-blur"
        >
          Debug Panel
        </Button>
      </div>
    )
  }

  const clearLogs = () => {
    setLogs([])
  }

  const refreshNavigationData = () => {
    // Invalidate all navigation-related queries
    queryClient.invalidateQueries({ queryKey: ['gameweekInfo'] })
    queryClient.invalidateQueries({ queryKey: ['availableGameweeks'] })
    queryClient.invalidateQueries({ queryKey: ['tokenBalances'] })
    queryClient.invalidateQueries({ queryKey: ['notifications'] })
    queryClient.invalidateQueries({ queryKey: ['userRank'] })
    queryClient.invalidateQueries({ queryKey: ['liveMatches'] })

    const timestamp = new Date().toLocaleTimeString()
    setLogs((prev) => [...prev, `${timestamp}: Navigation data refreshed`])
  }

  return (
    <div className="fixed bottom-4 right-4 w-96 max-h-[600px] z-50 bg-white border border-gray-200 rounded-lg shadow-lg">
      <div className="flex items-center justify-between p-3 border-b">
        <h3 className="font-semibold text-sm">Development Debug Panel</h3>
        <div className="flex gap-2">
          <Badge variant="outline" className="text-xs">
            DEV
          </Badge>
          <Button
            onClick={() => setIsVisible(false)}
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
          >
            ×
          </Button>
        </div>
      </div>

      <Tabs defaultValue="logs" className="w-full">
        <TabsList className="grid w-full grid-cols-4 text-xs">
          <TabsTrigger value="logs">Logs</TabsTrigger>
          <TabsTrigger value="nav">Navigation</TabsTrigger>
          <TabsTrigger value="system">System</TabsTrigger>
          <TabsTrigger value="help">Help</TabsTrigger>
        </TabsList>

        <TabsContent value="logs" className="p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium">Recent Activity ({logs.length})</span>
            <Button onClick={clearLogs} variant="ghost" size="sm" className="h-6 text-xs">
              Clear
            </Button>
          </div>
          <ScrollArea className="h-64">
            <div className="space-y-1">
              {logs
                .slice(-20)
                .reverse()
                .map((log, index) => (
                  <div key={index} className="text-xs p-2 bg-gray-50 rounded">
                    {log}
                  </div>
                ))}
              {logs.length === 0 && (
                <div className="text-xs text-gray-500 p-2">No activity yet...</div>
              )}
            </div>
          </ScrollArea>
        </TabsContent>

        <TabsContent value="nav" className="p-3">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium">Navigation Data</span>
              <Button
                onClick={refreshNavigationData}
                variant="outline"
                size="sm"
                className="h-6 text-xs"
              >
                Refresh Cache
              </Button>
            </div>

            <div className="space-y-2">
              <div className="text-xs">
                <strong>Loading:</strong> {isLoading ? 'Yes' : 'No'}
              </div>
              {gameweekInfo && (
                <>
                  <div className="text-xs">
                    <strong>Current GW:</strong> {gameweekInfo.currentGameweek}
                  </div>
                  <div className="text-xs">
                    <strong>Season:</strong> {gameweekInfo.season}
                  </div>
                  <div className="text-xs">
                    <strong>Deadline:</strong> {gameweekInfo.deadline}
                  </div>
                  <div className="text-xs">
                    <strong>Time Until:</strong> {gameweekInfo.timeUntilDeadline}
                  </div>
                </>
              )}
              {!gameweekInfo && !isLoading && (
                <div className="text-xs text-red-600">No gameweek data available</div>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="system" className="p-3">
          <div className="space-y-3">
            <div>
              <span className="text-xs font-medium">System Info</span>
              <div className="text-xs space-y-1 mt-1">
                <div>
                  <strong>Time:</strong> {systemInfo.timestamp}
                </div>
                <div>
                  <strong>Path:</strong> {systemInfo.url}
                </div>
                <div>
                  <strong>Viewport:</strong> {systemInfo.viewport}
                </div>
                <div>
                  <strong>Browser:</strong> {systemInfo.userAgent}
                </div>
              </div>
            </div>

            <div>
              <span className="text-xs font-medium">Environment</span>
              <div className="text-xs space-y-1 mt-1">
                <div>
                  <strong>Mode:</strong> {process.env.NODE_ENV}
                </div>
                <div>
                  <strong>Next.js:</strong> 15.3.0
                </div>
                <div>
                  <strong>React:</strong> 19
                </div>
              </div>
            </div>

            <div>
              <span className="text-xs font-medium">Features</span>
              <div className="flex flex-wrap gap-1 mt-1">
                <Badge variant="outline" className="text-xs">
                  Auth
                </Badge>
                <Badge variant="outline" className="text-xs">
                  3D Stadium
                </Badge>
                <Badge variant="outline" className="text-xs">
                  Real-time
                </Badge>
                <Badge variant="outline" className="text-xs">
                  PWA Ready
                </Badge>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="help" className="p-3">
          <div className="space-y-3">
            <div>
              <span className="text-xs font-medium">Keyboard Shortcuts</span>
              <div className="text-xs space-y-1 mt-1">
                <div>
                  <kbd className="px-1 py-0.5 bg-gray-100 rounded">Ctrl+Shift+D</kbd> Toggle Panel
                </div>
              </div>
            </div>

            <div>
              <span className="text-xs font-medium">Quick Links</span>
              <div className="text-xs space-y-1 mt-1">
                <div>
                  <a href="/dashboard" className="text-blue-600 hover:underline">
                    Dashboard
                  </a>
                </div>
                <div>
                  <a href="/onboarding" className="text-blue-600 hover:underline">
                    Onboarding
                  </a>
                </div>
                <div>
                  <a href="/register" className="text-blue-600 hover:underline">
                    Registration
                  </a>
                </div>
              </div>
            </div>

            <div>
              <span className="text-xs font-medium">API Endpoints</span>
              <div className="text-xs space-y-1 mt-1">
                <div className="font-mono">/api/auth/*</div>
                <div className="font-mono">/api/monitoring/*</div>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  )
}
